// System
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Components
import { TableColumnHeader } from '@Components/shared/tables/columns';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';

// Contracts
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { Filterable } from '@contracts/common/traits/Filterable';
import { ScheduledSendCreditTaskItemDto } from '@contracts/api/dto/scheduledSendCreditTask/ScheduledSendCreditTask';
import {
  ScheduledSendCreditTasksListColumns,
  ScheduledSendCreditTasksListFilter,
  scheduledSendCreditTasksListColumns,
} from '@utils/tables/scheduledSendCreditTasks/scheduledSendCreditTasksListFilter';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { parseISO } from 'date-fns';
import {
  convertMillisecondsToTimeParts,
  convertTimestampToISOString,
} from '@Components/shared/labels/DateLabel/DateLabel';

type HeaderOptions = {
  filter: Filterable<ScheduledSendCreditTasksListFilter>;
};

type Columns = TableColumnDescriptionArray<
  ScheduledSendCreditTaskItemDto,
  HeaderOptions,
  typeof scheduledSendCreditTasksListColumns
>;

type Column = TableColumnDescription<
  ScheduledSendCreditTaskItemDto,
  HeaderOptions,
  ScheduledSendCreditTasksListColumns
>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useScheduledSendCreditTasksTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'scheduledSendCreditTask.id',
          title: 'ID',
          sx: { minWidth: '120px', maxWidth: '200px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>{row?.id && <TruncateText text={row.id} maxLength={8} textStyles={styles.id} />}</>
          ),
        },
        {
          name: 'scheduledSendCreditTask.delayMs',
          title: 'Delay Ms',
          sx: { minWidth: '120px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{convertMillisecondsToTimeParts(row?.delayMs)}</Box>
          ),
        },
        {
          name: 'scheduledSendCreditTask.createdAt',
          title: 'Created At',
          sx: { minWidth: '140px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.createdAt && (
                <DateLabel date={new Date(parseISO(row?.createdAt))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'scheduledSendCreditTask.data.apiUrl',
          title: 'Api Url',
          sx: { minWidth: '120px', maxWidth: '250px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.apiUrl && (
                <TruncateText text={row.data?.apiUrl} maxLength={12} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'scheduledSendCreditTask.data.transactionId',
          title: 'Transaction Id',
          sx: { minWidth: '120px', maxWidth: '200px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.transactionId && (
                <TruncateText
                  text={row?.data?.transactionId}
                  maxLength={8}
                  textStyles={styles.id}
                />
              )}
            </>
          ),
        },
        {
          name: 'scheduledSendCreditTask.data.roundId',
          title: 'Round Id',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.roundId && (
                <TruncateText text={row?.data?.roundId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'scheduledSendCreditTask.data.amount',
          title: 'Amount',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.data?.amount}</Box>,
        },
        {
          name: 'scheduledSendCreditTask.data.language',
          title: 'Language',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.data?.language}</Box>,
        },
        {
          name: 'scheduledSendCreditTask.data.currency',
          title: 'Currency',
          sx: { minWidth: '120px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.data?.currency}</Box>,
        },
        {
          name: 'scheduledSendCreditTask.data.reason',
          title: 'Reason',
          sx: { minWidth: '100px', maxWidth: '200px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.data?.reason}</Box>,
        },
        {
          name: 'scheduledSendCreditTask.data.category',
          title: 'Category',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.data?.category}</Box>,
        },
        {
          name: 'scheduledSendCreditTask.data.providerId',
          title: 'Provider Id',
          sx: { minWidth: '120px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.providerId && (
                <TruncateText text={row?.data?.providerId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'scheduledSendCreditTask.data.gameId',
          title: 'Game Id',
          sx: { minWidth: '100px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.data?.gameId}</Box>,
        },
        {
          name: 'scheduledSendCreditTask.data.operation',
          title: 'Operation',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.data?.operation}</Box>,
        },
        {
          name: 'scheduledSendCreditTask.data.playerId',
          title: 'Player Id',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.playerId && (
                <TruncateText text={row?.data?.playerId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
        {
          name: 'scheduledSendCreditTask.data.multiplier',
          title: 'Multiplier',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.data?.multiplier}</Box>,
        },
        {
          name: 'scheduledSendCreditTask.data.betId',
          title: 'Bet Id',
          sx: { minWidth: '100px', maxWidth: '140px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <>
              {row?.data?.betId && (
                <TruncateText text={row?.data?.betId} maxLength={8} textStyles={styles.id} />
              )}
            </>
          ),
        },
      ] satisfies Columns,
    [ellipsis, styles.id]
  );
};
